/**
 * CombatSystem
 * 
 * Handles combat-related functionality including target engagement,
 * cannon effects, ship sinking visuals, and loot displays.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';
import { TILE_SIZE } from '../types';
import { ShipSprite } from './ShipRenderer';
import { drawCombatRange } from '../utils/RadiusRenderer';
import { RectanglePool, PoolableRectangle, releaseAfterTween } from '../utils/ObjectPool';
import { 
  getEffectConfig, 
  WeaponRarity, 
  SignatureElement, 
  EffectConfig,
  calculateVisualIntensity,
  shouldApplyEnhancement,
  ENHANCEMENT_THRESHOLDS
} from './CombatEffectsConfig';

// ============ INTERFACES ============

export interface CombatCallbacks {
  getShips: () => Map<string, ShipSprite>;
  getLocalShip: () => ShipSprite | null;
  hideTargetInfo: () => void;
  clearAllPathVisualizations: () => void;
  isInSafeZone: () => boolean;
}

export interface CannonEffectData {
  attackerId: string;
  targetId: string;
  result: {
    hit: boolean;
    damage: number;
    isCrit: boolean;
  };
  weaponRarity?: WeaponRarity;
  signatureElement?: SignatureElement;
}

// ============ COMBAT SYSTEM ============

export class CombatSystem {
  private scene: Phaser.Scene;
  private socket: Socket | null = null;
  private callbacks: CombatCallbacks;
  
  // Combat state
  private selectedTarget: string | null = null;
  private combatTrackingLine: Phaser.GameObjects.Graphics | null = null;
  private combatPathGraphics: Phaser.GameObjects.Graphics | null = null;
  private combatDestinationIndicator: Phaser.GameObjects.Rectangle | null = null;
  
  // Object pool for combat effects (reduces GC pressure)
  private effectPool: RectanglePool;

  constructor(scene: Phaser.Scene, callbacks: CombatCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    
    // Initialize effect pool (50 initial, 200 max)
    this.effectPool = new RectanglePool(scene, 50, 200);
    this.effectPool.init();
  }

  // ============ PUBLIC API ============

  public setSocket(socket: Socket | null): void {
    this.socket = socket;
  }

  public getSelectedTarget(): string | null {
    return this.selectedTarget;
  }

  /**
   * Clean up resources (call on scene shutdown)
   */
  public destroy(): void {
    this.effectPool.destroy();
  }

  /**
   * Get pool statistics for debugging
   */
  public getPoolStats(): { pooled: number; active: number; total: number } {
    return this.effectPool.getStats();
  }

  /**
   * Engage a target - tells server to pursue and auto-fire when in range
   */
  public engageTarget(shipId: string): void {
    const ships = this.callbacks.getShips();
    
    // Deselect previous target visually
    if (this.selectedTarget) {
      const prevShip = ships.get(this.selectedTarget);
      if (prevShip) {
        prevShip.hull.setStrokeStyle(2, 0xffffff);
      }
    }
    
    this.selectedTarget = shipId;
    
    // Clear ALL path visualizations - combat uses tracking line instead
    this.callbacks.clearAllPathVisualizations();
    
    // Highlight new target
    const ship = ships.get(shipId);
    if (ship) {
      ship.hull.setStrokeStyle(3, 0xff0000);
    }
    
    // Tell server to engage (pursue + auto-fire)
    console.log(`⚔️ Emitting shipEngage to server, targetId: ${shipId}, socket connected: ${this.socket?.connected}`);
    this.socket?.emit('shipEngage', { targetId: shipId });
  }

  /**
   * Disengage current target - stop pursuing and firing
   */
  public disengageTarget(): void {
    const ships = this.callbacks.getShips();
    
    if (this.selectedTarget) {
      const prevShip = ships.get(this.selectedTarget);
      if (prevShip) {
        prevShip.hull.setStrokeStyle(2, 0xffffff);
      }
    }
    
    this.selectedTarget = null;
    this.callbacks.hideTargetInfo();
    
    // Clear combat tracking line
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }
    
    // Tell server to disengage
    this.socket?.emit('shipDisengage');
  }

  /**
   * Update the combat tracking line - direct line from player to target
   * This updates every frame to track movement
   */
  public updateCombatTrackingLine(): void {
    // Clear existing line
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }
    
    const localShip = this.callbacks.getLocalShip();
    
    // Only show if we have a target and local ship
    if (!this.selectedTarget || !localShip) {
      return;
    }
    
    const ships = this.callbacks.getShips();
    const target = ships.get(this.selectedTarget);
    if (!target) {
      return;
    }
    
    // Create graphics if needed
    if (!this.combatTrackingLine) {
      this.combatTrackingLine = this.scene.add.graphics();
      this.combatTrackingLine.setDepth(55);
    }
    
    // Calculate distance
    const dx = target.x - localShip.x;
    const dy = target.y - localShip.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const distanceTiles = distance / TILE_SIZE;
    
    // Range check (5 tiles)
    const rangeTiles = 5;
    const inRange = distanceTiles <= rangeTiles;
    
    // Draw grid-based combat range indicator
    drawCombatRange(this.combatTrackingLine, localShip.x, localShip.y, rangeTiles, inRange);
    
    // Draw line - red if in range, orange if chasing
    const lineColor = inRange ? 0xff0000 : 0xff6600;
    this.combatTrackingLine.lineStyle(3, lineColor, 0.8);
    this.combatTrackingLine.moveTo(localShip.x, localShip.y);
    this.combatTrackingLine.lineTo(target.x, target.y);
    this.combatTrackingLine.strokePath();
    
    // Draw target reticle (corner brackets)
    this.combatTrackingLine.lineStyle(2, lineColor, 0.6);
    const reticleSize = TILE_SIZE * 1.5;
    const corners = [
      { x: target.x - reticleSize/2, y: target.y - reticleSize/2, dx: 6, dy: 0, dx2: 0, dy2: 6 },
      { x: target.x + reticleSize/2, y: target.y - reticleSize/2, dx: -6, dy: 0, dx2: 0, dy2: 6 },
      { x: target.x - reticleSize/2, y: target.y + reticleSize/2, dx: 6, dy: 0, dx2: 0, dy2: -6 },
      { x: target.x + reticleSize/2, y: target.y + reticleSize/2, dx: -6, dy: 0, dx2: 0, dy2: -6 },
    ];
    for (const c of corners) {
      this.combatTrackingLine.moveTo(c.x, c.y);
      this.combatTrackingLine.lineTo(c.x + c.dx, c.y + c.dy);
      this.combatTrackingLine.moveTo(c.x, c.y);
      this.combatTrackingLine.lineTo(c.x + c.dx2, c.y + c.dy2);
    }
    this.combatTrackingLine.strokePath();
  }

  /**
   * Show cannon fire effect (uses object pooling for performance)
   * Progressive enhancement: scales visually with weapon rarity and signature elements
   */
  public showCannonEffect(data: CannonEffectData): void {
    const ships = this.callbacks.getShips();
    const attacker = ships.get(data.attackerId);
    const target = ships.get(data.targetId);
    
    if (!attacker || !target) return;
    
    const config = getEffectConfig(data.weaponRarity, data.signatureElement);
    const intensity = calculateVisualIntensity(data.weaponRarity, data.signatureElement);
    
    // BASE EFFECTS (Common rarity, Level 1-9) - Always run
    // Muzzle flash (multiple expanding squares with rarity-based colors)
    const flashCount = Math.ceil(3 * config.muzzleFlashScale);
    for (let i = 0; i < flashCount; i++) {
      const colors = [config.primaryColor, config.secondaryColor, config.tertiaryColor];
      const flash = this.effectPool.acquire(
        attacker.x + (Math.random() - 0.5) * 8, 
        attacker.y + (Math.random() - 0.5) * 8, 
        12 + i * 4, 
        12 + i * 4, 
        colors[i % colors.length], 
        0.9 - i * 0.2
      );
      flash.setDepth(60);
      releaseAfterTween(this.scene, this.effectPool, flash, {
        alpha: 0,
        scaleX: (2 + i * 0.5) * config.muzzleFlashScale,
        scaleY: (2 + i * 0.5) * config.muzzleFlashScale,
        duration: 150 + i * 50,
      });
    }
    
    // Smoke particles from muzzle (scaled by rarity)
    const smokeCount = Math.ceil(4 * config.trailDensity);
    for (let i = 0; i < smokeCount; i++) {
      const smoke = this.effectPool.acquire(attacker.x, attacker.y, 6, 6, 0x666666, 0.6);
      smoke.setDepth(55);
      const angle = Math.random() * Math.PI * 2;
      const speed = 20 + Math.random() * 20;
      releaseAfterTween(this.scene, this.effectPool, smoke, {
        x: attacker.x + Math.cos(angle) * speed,
        y: attacker.y + Math.sin(angle) * speed,
        alpha: 0,
        scaleX: 2,
        scaleY: 2,
        duration: 400,
        delay: i * 30,
      });
    }
    
    // UNCOMMON+ ENHANCEMENTS (Level 10+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.UNCOMMON)) {
      // Subtle glow halo around muzzle flash
      const glow = this.effectPool.acquire(attacker.x, attacker.y, 20, 20, config.primaryColor, 0.3);
      glow.setDepth(59);
      releaseAfterTween(this.scene, this.effectPool, glow, {
        alpha: 0,
        scaleX: 3,
        scaleY: 3,
        duration: 250,
      });
    }
    
    // RARE+ ENHANCEMENTS (Level 25+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.RARE)) {
      // Additional layered muzzle flash for depth
      for (let i = 0; i < 2; i++) {
        const extraFlash = this.effectPool.acquire(
          attacker.x + (Math.random() - 0.5) * 12,
          attacker.y + (Math.random() - 0.5) * 12,
          16 + i * 6,
          16 + i * 6,
          config.secondaryColor,
          0.7 - i * 0.2
        );
        extraFlash.setDepth(61 + i);
        releaseAfterTween(this.scene, this.effectPool, extraFlash, {
          alpha: 0,
          scaleX: 2.5 + i * 0.5,
          scaleY: 2.5 + i * 0.5,
          duration: 200 + i * 50,
        });
      }
    }
    
    // EPIC+ ENHANCEMENTS (Level 40+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.EPIC)) {
      // Star-pattern muzzle flash (4 directional bursts)
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2;
        const distance = 15;
        const star = this.effectPool.acquire(
          attacker.x + Math.cos(angle) * 5,
          attacker.y + Math.sin(angle) * 5,
          8,
          8,
          config.tertiaryColor,
          0.8
        );
        star.setDepth(62);
        releaseAfterTween(this.scene, this.effectPool, star, {
          x: attacker.x + Math.cos(angle) * distance,
          y: attacker.y + Math.sin(angle) * distance,
          alpha: 0,
          scaleX: 0.3,
          scaleY: 0.3,
          duration: 200,
        });
      }
    }
    
    // Cannonball with colored glow
    const ball = this.effectPool.acquire(attacker.x, attacker.y, 8, 8, config.primaryColor);
    ball.setStrokeStyle(2, config.secondaryColor);
    ball.setDepth(65);
    
    // RARE+ ENHANCEMENT: Cannonball rotation
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.RARE)) {
      this.scene.tweens.add({
        targets: ball,
        angle: 360,
        duration: 300,
        ease: 'Linear',
      });
    }
    
    // Trail effect (density based on rarity)
    const trailInterval = setInterval(() => {
      if (!ball.poolActive) {
        clearInterval(trailInterval);
        return;
      }
      const trail = this.effectPool.acquire(ball.x, ball.y, 4, 4, config.primaryColor, 0.5);
      trail.setDepth(64);
      releaseAfterTween(this.scene, this.effectPool, trail, {
        alpha: 0,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 200,
      });
      
      // UNCOMMON+ ENHANCEMENT: Glow halo on trail particles
      if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.UNCOMMON)) {
        const trailGlow = this.effectPool.acquire(ball.x, ball.y, 8, 8, config.secondaryColor, 0.3);
        trailGlow.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, trailGlow, {
          alpha: 0,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 250,
        });
      }
      
      // LEGENDARY+ ENHANCEMENT: Element-specific trail effects
      if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.LEGENDARY) && data.signatureElement) {
        this.addElementTrailEffect(ball.x, ball.y, data.signatureElement, config);
      }
    }, Math.max(10, 30 / config.trailDensity));
    
    this.scene.tweens.add({
      targets: ball,
      x: target.x,
      y: target.y,
      duration: 300,
      ease: 'Linear',
      onComplete: () => {
        clearInterval(trailInterval);
        this.effectPool.release(ball as PoolableRectangle);
        
        if (data.result.hit) {
          this.showHitEffect(target, data.result, config, intensity, data.signatureElement);
        } else {
          this.showMissEffect(target, intensity);
        }
      },
    });
  }
  
  /**
   * Add element-specific trail effects for legendary weapons with signature moves
   */
  private addElementTrailEffect(x: number, y: number, element: SignatureElement, config: EffectConfig): void {
    switch (element) {
      case 'flame':
        // Fire ember particles
        const ember = this.effectPool.acquire(
          x + (Math.random() - 0.5) * 4,
          y + (Math.random() - 0.5) * 4,
          3,
          3,
          0xffa500,
          0.9
        );
        ember.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, ember, {
          y: y - 10,
          alpha: 0,
          scaleX: 0.2,
          scaleY: 0.2,
          duration: 300,
        });
        break;
      
      case 'storm':
        // Electric spark
        const spark = this.effectPool.acquire(x, y, 2, 2, 0x7df9ff, 1.0);
        spark.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, spark, {
          x: x + (Math.random() - 0.5) * 10,
          y: y + (Math.random() - 0.5) * 10,
          alpha: 0,
          duration: 150,
        });
        break;
      
      case 'tide':
        // Water bubble
        const bubble = this.effectPool.acquire(x, y, 4, 4, 0x00bfff, 0.6);
        bubble.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, bubble, {
          y: y + 8,
          alpha: 0,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 250,
        });
        break;
      
      case 'shadow':
        // Void particle
        const voidParticle = this.effectPool.acquire(x, y, 3, 3, 0x2f0047, 0.8);
        voidParticle.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, voidParticle, {
          alpha: 0,
          scaleX: 2,
          scaleY: 2,
          duration: 200,
        });
        break;
      
      case 'iron':
        // Metallic shard
        const shard = this.effectPool.acquire(x, y, 2, 4, 0xc0c0c0, 0.9);
        shard.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, shard, {
          x: x + (Math.random() - 0.5) * 8,
          y: y + (Math.random() - 0.5) * 8,
          alpha: 0,
          rotation: Math.random() * Math.PI,
          duration: 200,
        });
        break;
      
      case 'spirit':
        // Ethereal wisp
        const wisp = this.effectPool.acquire(x, y, 4, 4, 0x9932cc, 0.7);
        wisp.setDepth(63);
        releaseAfterTween(this.scene, this.effectPool, wisp, {
          x: x + Math.sin(Date.now() * 0.01) * 10,
          y: y + Math.cos(Date.now() * 0.01) * 10,
          alpha: 0,
          scaleX: 1.8,
          scaleY: 1.8,
          duration: 300,
        });
        break;
    }
  }

  /**
   * Show signature move effect with element-based visuals
   * Enhanced with element-specific visual signatures
   */
  public showSignatureMoveEffect(
    originX: number,
    originY: number,
    targetX: number,
    targetY: number,
    element: SignatureElement,
    aoeRadius: number = 0
  ): void {
    const config = getEffectConfig(undefined, element);
    const intensity = calculateVisualIntensity(undefined, element); // Element always gives intensity boost
    
    // BASE EFFECTS - Central explosion at origin
    const explosionCount = Math.ceil(5 * config.muzzleFlashScale);
    for (let i = 0; i < explosionCount; i++) {
      const colors = [config.primaryColor, config.secondaryColor, config.tertiaryColor];
      const explosion = this.effectPool.acquire(
        originX + (Math.random() - 0.5) * 12,
        originY + (Math.random() - 0.5) * 12,
        16 + i * 6,
        16 + i * 6,
        colors[i % colors.length],
        0.9 - i * 0.15
      );
      explosion.setDepth(80 + i);
      releaseAfterTween(this.scene, this.effectPool, explosion, {
        alpha: 0,
        scaleX: (2.5 + i * 0.7) * config.muzzleFlashScale,
        scaleY: (2.5 + i * 0.7) * config.muzzleFlashScale,
        duration: 300 + i * 80,
      });
    }
    
    // Radial particles from origin
    const particleCount = Math.ceil(12 * config.trailDensity);
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const distance = 40 + Math.random() * 40;
      const particle = this.effectPool.acquire(
        originX,
        originY,
        config.particleSize,
        config.particleSize,
        config.primaryColor,
        0.8
      );
      particle.setDepth(75);
      releaseAfterTween(this.scene, this.effectPool, particle, {
        x: originX + Math.cos(angle) * distance,
        y: originY + Math.sin(angle) * distance,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 600,
        ease: 'Quad.easeOut',
      });
    }
    
    // ENHANCED ELEMENT-SPECIFIC VISUALS
    this.addEnhancedElementSignature(originX, originY, element, config, intensity);
    
    // Enhanced AOE visualization with element-themed patterns
    if (aoeRadius > 0) {
      // Base AOE ring
      const ringGraphics = this.scene.add.graphics();
      ringGraphics.setDepth(70);
      ringGraphics.lineStyle(3, config.primaryColor, 0.8);
      ringGraphics.strokeCircleShape(new Phaser.Geom.Circle(originX, originY, aoeRadius * TILE_SIZE));
      
      this.scene.tweens.add({
        targets: ringGraphics,
        alpha: 0,
        duration: 800,
        onComplete: () => ringGraphics.destroy(),
      });
      
      // Enhanced: Element-themed AOE pattern
      const patternRing = this.scene.add.graphics();
      patternRing.setDepth(71);
      patternRing.lineStyle(2, config.secondaryColor, 0.6);
      
      // Draw element-specific pattern around AOE
      const segments = 16;
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const radius = aoeRadius * TILE_SIZE;
        const x1 = originX + Math.cos(angle) * radius;
        const y1 = originY + Math.sin(angle) * radius;
        const x2 = originX + Math.cos(angle) * (radius + 10);
        const y2 = originY + Math.sin(angle) * (radius + 10);
        
        patternRing.moveTo(x1, y1);
        patternRing.lineTo(x2, y2);
      }
      patternRing.strokePath();
      
      this.scene.tweens.add({
        targets: patternRing,
        alpha: 0,
        duration: 1000,
        delay: 200,
        onComplete: () => patternRing.destroy(),
      });
    }
    
    // Screen shake scales with element intensity (only if not in safe zone)
    if (!this.callbacks.isInSafeZone()) {
      this.scene.cameras.main.shake(300, config.screenShakeAmount);
    }
  }
  
  /**
   * Add enhanced element-specific signature visuals
   */
  private addEnhancedElementSignature(
    x: number, 
    y: number, 
    element: SignatureElement, 
    config: EffectConfig,
    intensity: number
  ): void {
    switch (element) {
      case 'flame':
        // Fire spirals
        for (let spiral = 0; spiral < 3; spiral++) {
          for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2 + spiral * (Math.PI / 3);
            const distance = 20 + i * 5;
            const flame = this.effectPool.acquire(
              x + Math.cos(angle) * distance * 0.3,
              y + Math.sin(angle) * distance * 0.3,
              5,
              5,
              i % 2 === 0 ? 0xff6b35 : 0xffa500,
              0.9
            );
            flame.setDepth(76 + spiral);
            releaseAfterTween(this.scene, this.effectPool, flame, {
              x: x + Math.cos(angle) * distance,
              y: y + Math.sin(angle) * distance - 15,
              alpha: 0,
              scaleX: 0.3,
              scaleY: 0.3,
              duration: 500 + i * 30,
              delay: spiral * 50,
            });
          }
        }
        // Ember particles
        for (let i = 0; i < 20; i++) {
          const ember = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 30,
            y + (Math.random() - 0.5) * 30,
            3,
            3,
            0xffa500,
            0.8
          );
          ember.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, ember, {
            y: y - 40 - Math.random() * 20,
            alpha: 0,
            scaleX: 0.2,
            scaleY: 0.2,
            duration: 800 + Math.random() * 400,
            delay: Math.random() * 200,
          });
        }
        break;
      
      case 'storm':
        // Lightning bolts radiating outward
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const lightning = this.effectPool.acquire(
            x,
            y,
            3,
            20,
            0x7df9ff,
            1.0
          );
          lightning.setDepth(76);
          releaseAfterTween(this.scene, this.effectPool, lightning, {
            x: x + Math.cos(angle) * 50,
            y: y + Math.sin(angle) * 50,
            alpha: 0,
            rotation: angle,
            duration: 250,
            delay: i * 30,
          });
        }
        // Electric sparks
        for (let i = 0; i < 30; i++) {
          const spark = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 40,
            y + (Math.random() - 0.5) * 40,
            2,
            2,
            0x00bfff,
            1.0
          );
          spark.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, spark, {
            x: spark.x + (Math.random() - 0.5) * 20,
            y: spark.y + (Math.random() - 0.5) * 20,
            alpha: 0,
            duration: 200 + Math.random() * 200,
            delay: Math.random() * 100,
          });
        }
        break;
      
      case 'tide':
        // Water spirals
        for (let spiral = 0; spiral < 2; spiral++) {
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2 + spiral * Math.PI;
            const distance = 15 + i * 4;
            const water = this.effectPool.acquire(
              x,
              y,
              5,
              5,
              i % 3 === 0 ? 0x1e90ff : 0x00bfff,
              0.7
            );
            water.setDepth(76);
            releaseAfterTween(this.scene, this.effectPool, water, {
              x: x + Math.cos(angle) * distance,
              y: y + Math.sin(angle) * distance,
              alpha: 0,
              scaleX: 1.5,
              scaleY: 1.5,
              duration: 600 + i * 25,
              delay: spiral * 100,
            });
          }
        }
        // Bubble effects
        for (let i = 0; i < 25; i++) {
          const bubble = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 50,
            y + (Math.random() - 0.5) * 50,
            4,
            4,
            0x87ceeb,
            0.6
          );
          bubble.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, bubble, {
            y: y - 30 - Math.random() * 20,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 700 + Math.random() * 300,
            delay: Math.random() * 200,
          });
        }
        break;
      
      case 'shadow':
        // Dark energy tendrils
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2;
          for (let j = 0; j < 8; j++) {
            const distance = 10 + j * 6;
            const tendril = this.effectPool.acquire(
              x + Math.cos(angle) * distance * 0.5,
              y + Math.sin(angle) * distance * 0.5,
              4,
              4,
              j % 2 === 0 ? 0x4b0082 : 0x2f0047,
              0.8 - j * 0.08
            );
            tendril.setDepth(76);
            releaseAfterTween(this.scene, this.effectPool, tendril, {
              x: x + Math.cos(angle) * distance,
              y: y + Math.sin(angle) * distance,
              alpha: 0,
              scaleX: 2,
              scaleY: 2,
              duration: 500 + j * 50,
              delay: i * 40,
            });
          }
        }
        // Void particles
        for (let i = 0; i < 20; i++) {
          const voidP = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 60,
            y + (Math.random() - 0.5) * 60,
            6,
            6,
            0x1a0033,
            0.9
          );
          voidP.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, voidP, {
            x: x,
            y: y,
            alpha: 0,
            scaleX: 0.2,
            scaleY: 0.2,
            duration: 600,
            delay: Math.random() * 200,
          });
        }
        break;
      
      case 'iron':
        // Metallic shards radiating outward
        for (let i = 0; i < 24; i++) {
          const angle = (i / 24) * Math.PI * 2;
          const shard = this.effectPool.acquire(
            x,
            y,
            3,
            8,
            i % 2 === 0 ? 0xc0c0c0 : 0x808080,
            0.9
          );
          shard.setDepth(76);
          releaseAfterTween(this.scene, this.effectPool, shard, {
            x: x + Math.cos(angle) * (40 + Math.random() * 20),
            y: y + Math.sin(angle) * (40 + Math.random() * 20),
            alpha: 0,
            rotation: Math.random() * Math.PI * 6,
            duration: 500 + Math.random() * 200,
            delay: i * 20,
          });
        }
        // Steel sparks
        for (let i = 0; i < 30; i++) {
          const spark = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 20,
            y + (Math.random() - 0.5) * 20,
            2,
            2,
            0xffffff,
            1.0
          );
          spark.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, spark, {
            x: spark.x + (Math.random() - 0.5) * 30,
            y: spark.y + (Math.random() - 0.5) * 30,
            alpha: 0,
            duration: 300,
            delay: Math.random() * 150,
          });
        }
        break;
      
      case 'spirit':
        // Ethereal wisps in spiral pattern
        for (let i = 0; i < 20; i++) {
          const angle = (i / 20) * Math.PI * 4; // Double spiral
          const distance = 20 + i * 3;
          const wisp = this.effectPool.acquire(
            x + Math.cos(angle) * distance * 0.3,
            y + Math.sin(angle) * distance * 0.3,
            6,
            6,
            i % 2 === 0 ? 0x9932cc : 0x8b008b,
            0.7
          );
          wisp.setDepth(76);
          releaseAfterTween(this.scene, this.effectPool, wisp, {
            x: x + Math.cos(angle) * distance,
            y: y + Math.sin(angle) * distance,
            alpha: 0,
            scaleX: 2,
            scaleY: 2,
            duration: 800 + i * 30,
          });
        }
        // Mystical orbs
        for (let i = 0; i < 12; i++) {
          const angle = (i / 12) * Math.PI * 2;
          const orb = this.effectPool.acquire(
            x + Math.cos(angle) * 25,
            y + Math.sin(angle) * 25,
            8,
            8,
            0x4b0082,
            0.8
          );
          orb.setDepth(77);
          releaseAfterTween(this.scene, this.effectPool, orb, {
            x: x + Math.cos(angle) * 50,
            y: y + Math.sin(angle) * 50,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 700,
            delay: i * 50,
          });
        }
        break;
    }
  }

  /**
   * Show dramatic sinking effect for a ship
   * Progressive enhancement: more dramatic effects for higher-level ships
   */
  public showShipSinkingEffect(
    ship: ShipSprite, 
    shipName: string, 
    pieces: number,
    weaponRarity?: WeaponRarity
  ): void {
    const x = ship.x;
    const y = ship.y;
    const intensity = calculateVisualIntensity(weaponRarity);
    
    // BASE EFFECTS (Common rarity) - Always run
    // Flash the ship red
    this.scene.tweens.add({
      targets: ship.hull,
      fillColor: { from: ship.hull.fillColor, to: 0xff0000 },
      duration: 100,
      yoyo: true,
      repeat: 3,
    });
    
    // Explosion effect - multiple expanding squares (pooled)
    for (let i = 0; i < 5; i++) {
      const explosion = this.effectPool.acquire(x, y, 16 + i * 8, 16 + i * 8, 0xff6600, 0.8 - i * 0.1);
      explosion.setDepth(100 + i);
      
      releaseAfterTween(this.scene, this.effectPool, explosion, {
        scaleX: 2 + i * 0.5,
        scaleY: 2 + i * 0.5,
        alpha: 0,
        duration: 400 + i * 100,
        delay: i * 50,
        ease: 'Quad.easeOut',
      });
    }
    
    // Fire particles (pooled)
    for (let i = 0; i < 12; i++) {
      const fire = this.effectPool.acquire(
        x + (Math.random() - 0.5) * 20,
        y + (Math.random() - 0.5) * 20,
        6,
        6,
        Math.random() > 0.5 ? 0xff4500 : 0xffa500,
        0.9
      );
      fire.setDepth(105);
      
      const angle = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 40;
      
      releaseAfterTween(this.scene, this.effectPool, fire, {
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist - 20,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 600 + Math.random() * 400,
        delay: Math.random() * 200,
        ease: 'Quad.easeOut',
      });
    }
    
    // Debris/wood particles (pooled)
    for (let i = 0; i < 8; i++) {
      const debris = this.effectPool.acquire(
        x + (Math.random() - 0.5) * 16,
        y + (Math.random() - 0.5) * 16,
        4 + Math.random() * 4,
        4 + Math.random() * 4,
        0x8b4513,
        0.9
      );
      debris.setDepth(104);
      
      const angle = Math.random() * Math.PI * 2;
      const dist = 40 + Math.random() * 50;
      
      releaseAfterTween(this.scene, this.effectPool, debris, {
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        rotation: Math.random() * Math.PI * 4,
        duration: 800 + Math.random() * 400,
        delay: 100 + Math.random() * 200,
        ease: 'Quad.easeOut',
      });
    }
    
    // RARE+ ENHANCEMENTS (Level 25+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.RARE)) {
      // Multi-stage explosion sequence
      for (let stage = 0; stage < 3; stage++) {
        setTimeout(() => {
          for (let i = 0; i < 3; i++) {
            const stageExplosion = this.effectPool.acquire(
              x + (Math.random() - 0.5) * 25,
              y + (Math.random() - 0.5) * 25,
              20 + i * 6,
              20 + i * 6,
              stage % 2 === 0 ? 0xff6600 : 0xff4500,
              0.7
            );
            stageExplosion.setDepth(106 + stage);
            releaseAfterTween(this.scene, this.effectPool, stageExplosion, {
              scaleX: 2.5 + i * 0.5,
              scaleY: 2.5 + i * 0.5,
              alpha: 0,
              duration: 350,
            });
          }
        }, stage * 200);
      }
      
      // More debris variety with rotation
      for (let i = 0; i < 12; i++) {
        const largeDebris = this.effectPool.acquire(
          x + (Math.random() - 0.5) * 20,
          y + (Math.random() - 0.5) * 20,
          6 + Math.random() * 6,
          6 + Math.random() * 6,
          0x8b4513,
          0.9
        );
        largeDebris.setDepth(104);
        const angle = Math.random() * Math.PI * 2;
        const dist = 50 + Math.random() * 60;
        releaseAfterTween(this.scene, this.effectPool, largeDebris, {
          x: x + Math.cos(angle) * dist,
          y: y + Math.sin(angle) * dist,
          alpha: 0,
          rotation: Math.random() * Math.PI * 8,
          duration: 1000 + Math.random() * 500,
          delay: 150 + Math.random() * 250,
          ease: 'Quad.easeOut',
        });
      }
    }
    
    // EPIC+ ENHANCEMENTS (Level 40+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.EPIC)) {
      // Water splash rings
      for (let i = 0; i < 3; i++) {
        const splashRing = this.scene.add.graphics();
        splashRing.setDepth(103);
        splashRing.lineStyle(3, 0x3498db, 0.7 - i * 0.2);
        splashRing.strokeCircle(x, y, 15);
        
        this.scene.tweens.add({
          targets: splashRing,
          alpha: 0,
          duration: 800 + i * 200,
          delay: 300 + i * 150,
          onUpdate: (tween) => {
            const progress = tween.progress;
            const radius = 15 + progress * 60;
            splashRing.clear();
            splashRing.lineStyle(3, 0x3498db, (0.7 - i * 0.2) * (1 - progress));
            splashRing.strokeCircle(x, y, radius);
          },
          onComplete: () => splashRing.destroy(),
        });
      }
      
      // More dramatic sinking with rotation
      this.scene.tweens.add({
        targets: ship,
        rotation: ship.rotation + Math.PI / 4,
        duration: 1500,
        delay: 500,
        ease: 'Quad.easeIn',
      });
    }
    
    // LEGENDARY+ ENHANCEMENTS (Level 50+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.LEGENDARY)) {
      // Large final explosion burst
      setTimeout(() => {
        for (let i = 0; i < 8; i++) {
          const finalBurst = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 30,
            y + (Math.random() - 0.5) * 30,
            30 + i * 8,
            30 + i * 8,
            0xffd700,
            0.8 - i * 0.08
          );
          finalBurst.setDepth(110 + i);
          releaseAfterTween(this.scene, this.effectPool, finalBurst, {
            scaleX: 3 + i * 0.5,
            scaleY: 3 + i * 0.5,
            alpha: 0,
            duration: 500 + i * 100,
          });
        }
        
        // Enhanced water effects (bubbles)
        for (let i = 0; i < 20; i++) {
          const bubble = this.effectPool.acquire(
            x + (Math.random() - 0.5) * 40,
            y + (Math.random() - 0.5) * 40,
            4,
            4,
            0x87ceeb,
            0.6
          );
          bubble.setDepth(102);
          releaseAfterTween(this.scene, this.effectPool, bubble, {
            y: y - 30 - Math.random() * 20,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 800 + Math.random() * 400,
            delay: Math.random() * 300,
          });
        }
      }, 600);
    }
    
    // Sinking animation - ship fades and shrinks
    this.scene.tweens.add({
      targets: ship,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      duration: 2000,
      delay: 500,
      ease: 'Quad.easeIn',
    });
    
    // "DESTROYED" text
    const destroyedText = this.scene.add.text(x, y - 40, 'DESTROYED', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '16px',
      color: '#ff4444',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    destroyedText.setOrigin(0.5);
    destroyedText.setDepth(110);
    
    this.scene.tweens.add({
      targets: destroyedText,
      y: y - 80,
      alpha: 0,
      duration: 2000,
      delay: 500,
      ease: 'Quad.easeOut',
      onComplete: () => destroyedText.destroy(),
    });
    
    // Ship name text
    const nameText = this.scene.add.text(x, y - 20, shipName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    });
    nameText.setOrigin(0.5);
    nameText.setDepth(109);
    
    this.scene.tweens.add({
      targets: nameText,
      y: y - 60,
      alpha: 0,
      duration: 1500,
      delay: 300,
      onComplete: () => nameText.destroy(),
    });
    
    // Berry reward text (if any)
    if (pieces > 0) {
      const safePieces: number = typeof pieces === 'number' && !Number.isNaN(pieces) ? pieces : 0;
      const berryText = this.scene.add.text(x, y + 10, `+${safePieces.toLocaleString()} ₱`, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '14px',
        color: '#f39c12',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      });
      berryText.setOrigin(0.5);
      berryText.setDepth(109);
      
      this.scene.tweens.add({
        targets: berryText,
        y: y - 30,
        alpha: 0,
        duration: 2000,
        delay: 800,
        ease: 'Quad.easeOut',
        onComplete: () => berryText.destroy(),
      });
    }
    
    // Screen shake (only if not in safe zone)
    if (!this.callbacks.isInSafeZone()) {
      this.scene.cameras.main.shake(300, 0.01);
    }
  }


  /**
   * Show loot gained effect - floating text on screen
   */
  public showLootGainedEffect(pieces: number, bounty: number, xp: number = 0, newLevel?: number): void {
    const safePieces: number = typeof pieces === 'number' && !Number.isNaN(pieces) ? pieces : 0;
    const safeBounty: number = typeof bounty === 'number' && !Number.isNaN(bounty) ? bounty : 0;
    const { width } = this.scene.cameras.main;

    // Pieces gained text (optional)
    let lootText: Phaser.GameObjects.Text | null = null;
    if (safePieces > 0) {
      lootText = this.scene.add.text(width / 2, 80, `+${safePieces.toLocaleString()} ₱`, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '28px',
        color: '#f39c12',
        fontStyle: 'bold',
      });
      lootText.setOrigin(0.5);
      lootText.setScrollFactor(0);
      lootText.setDepth(150);
      lootText.setAlpha(0);
      lootText.setScale(0.5);
    }
    
    // Bounty sub-text if applicable
    let bountyText: Phaser.GameObjects.Text | null = null;
    if (safeBounty > 0) {
      bountyText = this.scene.add.text(width / 2, 115, `(Bounty: ${safeBounty.toLocaleString()}₱)`, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '14px',
        color: '#e67e22',
        stroke: '#000000',
        strokeThickness: 2,
      });
      bountyText.setOrigin(0.5);
      bountyText.setScrollFactor(0);
      bountyText.setDepth(150);
      bountyText.setAlpha(0);
    }
    
    if (lootText) {
      // Pop in animation
      this.scene.tweens.add({
        targets: lootText,
        alpha: 1,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 200,
        ease: 'Back.easeOut',
        onComplete: () => {
          // Settle animation
          this.scene.tweens.add({
            targets: lootText,
            scaleX: 1,
            scaleY: 1,
            duration: 100,
          });
        },
      });
    }
    
    if (bountyText) {
      this.scene.tweens.add({
        targets: bountyText,
        alpha: 1,
        duration: 300,
        delay: 100,
      });
    }
    
    if (lootText) {
      // Float up and fade out
      this.scene.tweens.add({
        targets: lootText,
        y: 40,
        alpha: 0,
        duration: 2000,
        delay: 1000,
        ease: 'Sine.easeIn',
        onComplete: () => lootText?.destroy(),
      });
    }
    
    if (bountyText) {
      this.scene.tweens.add({
        targets: bountyText,
        y: 75,
        alpha: 0,
        duration: 2000,
        delay: 1000,
        ease: 'Sine.easeIn',
        onComplete: () => bountyText?.destroy(),
      });
    }
    
    // XP gained text
    if (xp > 0) {
      const xpText = this.scene.add.text(width / 2, 135, `+${xp} XP`, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '16px',
        color: '#3498db',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      });
      xpText.setOrigin(0.5);
      xpText.setScrollFactor(0);
      xpText.setDepth(150);
      xpText.setAlpha(0);
      
      this.scene.tweens.add({
        targets: xpText,
        alpha: 1,
        duration: 300,
        delay: 200,
      });
      
      this.scene.tweens.add({
        targets: xpText,
        y: 95,
        alpha: 0,
        duration: 2000,
        delay: 1200,
        ease: 'Sine.easeIn',
        onComplete: () => xpText.destroy(),
      });
    }
    
    // Level up effect
    if (newLevel) {
      this.showLevelUpEffect(newLevel);
    }
    
    // Gold particle effect
    this.showGoldParticles();
  }

  /**
   * Clear combat path visualization
   */
  public clearCombatPath(): void {
    if (this.combatPathGraphics) {
      this.combatPathGraphics.destroy();
      this.combatPathGraphics = null;
    }
    if (this.combatDestinationIndicator) {
      this.combatDestinationIndicator.destroy();
      this.combatDestinationIndicator = null;
    }
  }

  /**
   * Show combat path visualization
   */
  public showCombatPath(path: Array<{ tileX: number; tileY: number }>, targetTile: { tileX: number; tileY: number } | null): void {
    this.clearCombatPath();
    
    if (!path || path.length === 0) {
      return;
    }
    
    const localShip = this.callbacks.getLocalShip();
    const lineColor = 0xff0000;
    const fillColor = 0xff0000;
    
    // Create path graphics
    const graphics = this.scene.add.graphics();
    graphics.setDepth(50);
    
    // Draw path line
    if (localShip) {
      const startX = path[0].tileX * TILE_SIZE + TILE_SIZE / 2;
      const startY = path[0].tileY * TILE_SIZE + TILE_SIZE / 2;
      
      graphics.lineStyle(2, lineColor, 0.6);
      graphics.moveTo(localShip.x, localShip.y);
      graphics.lineTo(startX, startY);
      
      for (let i = 0; i < path.length - 1; i++) {
        const fromX = path[i].tileX * TILE_SIZE + TILE_SIZE / 2;
        const fromY = path[i].tileY * TILE_SIZE + TILE_SIZE / 2;
        const toX = path[i + 1].tileX * TILE_SIZE + TILE_SIZE / 2;
        const toY = path[i + 1].tileY * TILE_SIZE + TILE_SIZE / 2;
        
        graphics.moveTo(fromX, fromY);
        graphics.lineTo(toX, toY);
      }
      graphics.strokePath();
      
      // Draw waypoint dots
      graphics.fillStyle(fillColor, 0.5);
      for (const point of path) {
        const worldX = point.tileX * TILE_SIZE + TILE_SIZE / 2;
        const worldY = point.tileY * TILE_SIZE + TILE_SIZE / 2;
        graphics.fillRect(worldX - 4, worldY - 4, 8, 8);
      }
    }
    
    this.combatPathGraphics = graphics;
    
    // Show destination indicator
    if (targetTile) {
      const destX = targetTile.tileX * TILE_SIZE + TILE_SIZE / 2;
      const destY = targetTile.tileY * TILE_SIZE + TILE_SIZE / 2;
      
      const indicator = this.scene.add.rectangle(destX, destY, TILE_SIZE, TILE_SIZE, fillColor, 0.4);
      indicator.setStrokeStyle(2, lineColor);
      indicator.setDepth(5);
      
      // Pulsing animation
      this.scene.tweens.add({
        targets: indicator,
        alpha: 0.2,
        yoyo: true,
        repeat: -1,
        duration: 500,
      });
      
      this.combatDestinationIndicator = indicator;
    }
  }

  // ============ PRIVATE METHODS ============

  private showHitEffect(
    target: ShipSprite, 
    result: { damage: number; isCrit: boolean }, 
    config: EffectConfig,
    intensity: number = 1.0,
    element?: SignatureElement
  ): void {
    // BASE EFFECTS (Common rarity) - Always run
    // Impact explosion - multiple expanding squares with rarity-based colors (pooled)
    const impactCount = Math.ceil(4 * config.impactIntensity);
    for (let i = 0; i < impactCount; i++) {
      const impactColors = [config.primaryColor, config.secondaryColor, config.tertiaryColor, 0xffffff];
      const impact = this.effectPool.acquire(
        target.x + (Math.random() - 0.5) * 8,
        target.y + (Math.random() - 0.5) * 8,
        8 + i * 4,
        8 + i * 4,
        impactColors[i % impactColors.length],
        0.9 - i * 0.15
      );
      impact.setDepth(70 - i);
      releaseAfterTween(this.scene, this.effectPool, impact, {
        alpha: 0,
        scaleX: (3 + i) * config.impactIntensity,
        scaleY: (3 + i) * config.impactIntensity,
        duration: 200 + i * 100,
        delay: i * 30,
      });
    }
    
    // Debris particles (pooled)
    const debrisCount = Math.ceil(6 * config.impactIntensity);
    for (let i = 0; i < debrisCount; i++) {
      const debris = this.effectPool.acquire(target.x, target.y, 4, 4, 0x8b4513, 0.8);
      debris.setDepth(68);
      const angle = (i / debrisCount) * Math.PI * 2;
      const dist = 20 + Math.random() * 30;
      releaseAfterTween(this.scene, this.effectPool, debris, {
        x: target.x + Math.cos(angle) * dist,
        y: target.y + Math.sin(angle) * dist - 10,
        alpha: 0,
        rotation: Math.random() * Math.PI * 2,
        duration: 500,
        ease: 'Quad.easeOut',
      });
    }
    
    // UNCOMMON+ ENHANCEMENTS (Level 10+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.UNCOMMON)) {
      // Subtle impact glow on ship hull
      const impactGlow = this.effectPool.acquire(target.x, target.y, 24, 24, config.primaryColor, 0.4);
      impactGlow.setDepth(69);
      releaseAfterTween(this.scene, this.effectPool, impactGlow, {
        alpha: 0,
        scaleX: 2.5,
        scaleY: 2.5,
        duration: 300,
      });
    }
    
    // RARE+ ENHANCEMENTS (Level 25+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.RARE)) {
      // Impact shockwave rings
      for (let i = 0; i < 2; i++) {
        const ring = this.scene.add.graphics();
        ring.setDepth(71);
        ring.lineStyle(2, config.primaryColor, 0.8 - i * 0.2);
        ring.strokeCircle(target.x, target.y, 10);
        
        this.scene.tweens.add({
          targets: ring,
          alpha: 0,
          duration: 400 + i * 100,
          delay: i * 50,
          onUpdate: (tween) => {
            const progress = tween.progress;
            const radius = 10 + progress * 40;
            ring.clear();
            ring.lineStyle(2, config.primaryColor, (0.8 - i * 0.2) * (1 - progress));
            ring.strokeCircle(target.x, target.y, radius);
          },
          onComplete: () => ring.destroy(),
        });
      }
      
      // Element-colored sparks
      if (element) {
        const sparkCount = 8;
        for (let i = 0; i < sparkCount; i++) {
          const angle = (i / sparkCount) * Math.PI * 2;
          const spark = this.effectPool.acquire(
            target.x,
            target.y,
            3,
            3,
            config.secondaryColor,
            0.9
          );
          spark.setDepth(72);
          releaseAfterTween(this.scene, this.effectPool, spark, {
            x: target.x + Math.cos(angle) * 25,
            y: target.y + Math.sin(angle) * 25,
            alpha: 0,
            scaleX: 0.3,
            scaleY: 0.3,
            duration: 300,
          });
        }
      }
    }
    
    // EPIC+ ENHANCEMENTS (Level 40+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.EPIC)) {
      // Multi-stage impact explosion
      for (let stage = 0; stage < 2; stage++) {
        setTimeout(() => {
          const secondaryImpact = this.effectPool.acquire(
            target.x + (Math.random() - 0.5) * 12,
            target.y + (Math.random() - 0.5) * 12,
            16,
            16,
            config.tertiaryColor,
            0.7
          );
          secondaryImpact.setDepth(73);
          releaseAfterTween(this.scene, this.effectPool, secondaryImpact, {
            alpha: 0,
            scaleX: 3,
            scaleY: 3,
            duration: 250,
          });
        }, stage * 100);
      }
      
      // Shrapnel particles
      const shrapnelCount = 12;
      for (let i = 0; i < shrapnelCount; i++) {
        const angle = (i / shrapnelCount) * Math.PI * 2 + Math.random() * 0.3;
        const shrapnel = this.effectPool.acquire(target.x, target.y, 2, 4, 0xaaaaaa, 0.9);
        shrapnel.setDepth(72);
        releaseAfterTween(this.scene, this.effectPool, shrapnel, {
          x: target.x + Math.cos(angle) * (30 + Math.random() * 20),
          y: target.y + Math.sin(angle) * (30 + Math.random() * 20),
          alpha: 0,
          rotation: Math.random() * Math.PI * 4,
          duration: 400,
          ease: 'Quad.easeOut',
        });
      }
    }
    
    // LEGENDARY+ ENHANCEMENTS (Level 50+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.LEGENDARY) && element) {
      // Element-specific impact effects
      this.addElementImpactEffect(target.x, target.y, element, config);
    }
    
    // Screen shake for crits (scales with weapon power, only if not in safe zone)
    if (result.isCrit && !this.callbacks.isInSafeZone()) {
      this.scene.cameras.main.shake(200, config.screenShakeAmount);
    }
    
    // Damage text
    const dmgText = this.scene.add.text(target.x, target.y - 30, 
      `${result.isCrit ? 'CRIT! ' : ''}-${result.damage}`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: result.isCrit ? '18px' : '14px',
        color: result.isCrit ? '#ff0000' : '#ffffff',
        backgroundColor: '#000000cc',
        padding: { x: 6, y: 3 },
        fontStyle: 'bold',
      }
    );
    dmgText.setOrigin(0.5);
    dmgText.setDepth(100);
    
    // Pop-in effect
    dmgText.setScale(0.5);
    this.scene.tweens.add({
      targets: dmgText,
      scaleX: 1,
      scaleY: 1,
      duration: 100,
      ease: 'Back.easeOut',
    });
    
    this.scene.tweens.add({
      targets: dmgText,
      y: target.y - 60,
      alpha: 0,
      duration: 1200,
      delay: 300,
      onComplete: () => dmgText.destroy(),
    });
  }
  
  /**
   * Add element-specific impact effects for legendary weapons
   */
  private addElementImpactEffect(x: number, y: number, element: SignatureElement, config: EffectConfig): void {
    switch (element) {
      case 'flame':
        // Fire burst
        for (let i = 0; i < 12; i++) {
          const angle = (i / 12) * Math.PI * 2;
          const flame = this.effectPool.acquire(
            x,
            y,
            6,
            6,
            i % 2 === 0 ? 0xff4500 : 0xffa500,
            0.9
          );
          flame.setDepth(74);
          releaseAfterTween(this.scene, this.effectPool, flame, {
            x: x + Math.cos(angle) * 35,
            y: y + Math.sin(angle) * 35 - 15,
            alpha: 0,
            scaleX: 0.3,
            scaleY: 0.3,
            duration: 500,
          });
        }
        break;
      
      case 'storm':
        // Lightning chains
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2;
          const lightning = this.effectPool.acquire(
            x,
            y,
            2,
            12,
            0x7df9ff,
            1.0
          );
          lightning.setDepth(74);
          releaseAfterTween(this.scene, this.effectPool, lightning, {
            x: x + Math.cos(angle) * 30,
            y: y + Math.sin(angle) * 30,
            alpha: 0,
            rotation: angle,
            duration: 200,
          });
        }
        break;
      
      case 'tide':
        // Water splash
        for (let i = 0; i < 16; i++) {
          const angle = (i / 16) * Math.PI * 2;
          const water = this.effectPool.acquire(x, y, 4, 4, 0x00bfff, 0.7);
          water.setDepth(74);
          releaseAfterTween(this.scene, this.effectPool, water, {
            x: x + Math.cos(angle) * 40,
            y: y + Math.sin(angle) * 40,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 400,
          });
        }
        break;
      
      case 'shadow':
        // Void explosion
        const voidBurst = this.effectPool.acquire(x, y, 40, 40, 0x2f0047, 0.6);
        voidBurst.setDepth(74);
        releaseAfterTween(this.scene, this.effectPool, voidBurst, {
          alpha: 0,
          scaleX: 3,
          scaleY: 3,
          duration: 400,
        });
        break;
      
      case 'iron':
        // Metal fragments
        for (let i = 0; i < 20; i++) {
          const angle = Math.random() * Math.PI * 2;
          const fragment = this.effectPool.acquire(x, y, 3, 6, 0xc0c0c0, 0.9);
          fragment.setDepth(74);
          releaseAfterTween(this.scene, this.effectPool, fragment, {
            x: x + Math.cos(angle) * (25 + Math.random() * 20),
            y: y + Math.sin(angle) * (25 + Math.random() * 20),
            alpha: 0,
            rotation: Math.random() * Math.PI * 6,
            duration: 500,
          });
        }
        break;
      
      case 'spirit':
        // Ethereal burst
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const wisp = this.effectPool.acquire(x, y, 8, 8, 0x9932cc, 0.7);
          wisp.setDepth(74);
          releaseAfterTween(this.scene, this.effectPool, wisp, {
            x: x + Math.cos(angle) * 45,
            y: y + Math.sin(angle) * 45,
            alpha: 0,
            scaleX: 2,
            scaleY: 2,
            duration: 600,
          });
        }
        break;
    }
  }

  private showMissEffect(target: ShipSprite, intensity: number = 1.0): void {
    // BASE EFFECTS (Common rarity) - Always run
    // Miss effect - splash in water
    const splash = this.scene.add.text(target.x, target.y, 'MISS', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#888888',
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 },
    });
    splash.setOrigin(0.5);
    splash.setDepth(100);
    
    // Water splash particles
    for (let i = 0; i < 4; i++) {
      const water = this.scene.add.rectangle(target.x, target.y, 4, 4, 0x3498db, 0.7);
      water.setDepth(65);
      const angle = (i / 4) * Math.PI * 2;
      this.scene.tweens.add({
        targets: water,
        x: target.x + Math.cos(angle) * 15,
        y: target.y + Math.sin(angle) * 15 - 10,
        alpha: 0,
        duration: 400,
        onComplete: () => water.destroy(),
      });
    }
    
    // UNCOMMON+ ENHANCEMENTS (Level 10+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.UNCOMMON)) {
      // Water ripple effect
      const ripple = this.scene.add.graphics();
      ripple.setDepth(64);
      ripple.lineStyle(2, 0x3498db, 0.6);
      ripple.strokeCircle(target.x, target.y, 8);
      
      this.scene.tweens.add({
        targets: ripple,
        alpha: 0,
        duration: 500,
        onUpdate: (tween) => {
          const progress = tween.progress;
          const radius = 8 + progress * 25;
          ripple.clear();
          ripple.lineStyle(2, 0x3498db, 0.6 * (1 - progress));
          ripple.strokeCircle(target.x, target.y, radius);
        },
        onComplete: () => ripple.destroy(),
      });
    }
    
    // RARE+ ENHANCEMENTS (Level 25+)
    if (shouldApplyEnhancement(intensity, ENHANCEMENT_THRESHOLDS.RARE)) {
      // Additional splash particles and ripple rings
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const splashParticle = this.effectPool.acquire(
          target.x,
          target.y,
          3,
          3,
          0x3498db,
          0.8
        );
        splashParticle.setDepth(66);
        releaseAfterTween(this.scene, this.effectPool, splashParticle, {
          x: target.x + Math.cos(angle) * 20,
          y: target.y + Math.sin(angle) * 20 - 5,
          alpha: 0,
          scaleX: 0.5,
          scaleY: 0.5,
          duration: 350,
        });
      }
      
      // Second ripple ring
      const ripple2 = this.scene.add.graphics();
      ripple2.setDepth(64);
      ripple2.lineStyle(1, 0x5dade2, 0.5);
      ripple2.strokeCircle(target.x, target.y, 12);
      
      this.scene.tweens.add({
        targets: ripple2,
        alpha: 0,
        duration: 600,
        delay: 100,
        onUpdate: (tween) => {
          const progress = tween.progress;
          const radius = 12 + progress * 30;
          ripple2.clear();
          ripple2.lineStyle(1, 0x5dade2, 0.5 * (1 - progress));
          ripple2.strokeCircle(target.x, target.y, radius);
        },
        onComplete: () => ripple2.destroy(),
      });
    }
    
    this.scene.tweens.add({
      targets: splash,
      y: target.y - 30,
      alpha: 0,
      duration: 800,
      onComplete: () => splash.destroy(),
    });
  }

  private showLevelUpEffect(newLevel: number): void {
    const { width } = this.scene.cameras.main;
    
    const levelUpText = this.scene.add.text(width / 2, 160, `LEVEL UP! Lv.${newLevel}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '24px',
      color: '#2ecc71',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    levelUpText.setOrigin(0.5);
    levelUpText.setScrollFactor(0);
    levelUpText.setDepth(151);
    levelUpText.setAlpha(0);
    levelUpText.setScale(0.5);
    
    // Pop in with scale
    this.scene.tweens.add({
      targets: levelUpText,
      alpha: 1,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 300,
      delay: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: levelUpText,
          scaleX: 1,
          scaleY: 1,
          duration: 150,
        });
      },
    });
    
    // Fade out
    this.scene.tweens.add({
      targets: levelUpText,
      y: 120,
      alpha: 0,
      duration: 2500,
      delay: 2000,
      ease: 'Sine.easeIn',
      onComplete: () => levelUpText.destroy(),
    });
    
    // No screen flash - removed for epilepsy safety
  }

  private showGoldParticles(): void {
    const { width } = this.scene.cameras.main;
    
    for (let i = 0; i < 8; i++) {
      const coin = this.scene.add.rectangle(
        width / 2 + (Math.random() - 0.5) * 100,
        80,
        6,
        6,
        0xf39c12,
        0.8
      );
      coin.setScrollFactor(0);
      coin.setDepth(149);
      
      this.scene.tweens.add({
        targets: coin,
        y: 80 + Math.random() * 40 - 60,
        x: coin.x + (Math.random() - 0.5) * 60,
        alpha: 0,
        duration: 800 + Math.random() * 400,
        delay: i * 50,
        ease: 'Sine.easeOut',
        onComplete: () => coin.destroy(),
      });
    }
  }
}
